/**
 * ThemeContext
 * Shared theme state across all components
 */

import { createContext, useContext, useEffect, ReactNode } from 'react'

// Light mode was removed — the site is dark-only with the warm museum
// palette. The type is kept narrow so any leftover light-only branches
// in components fail at compile time instead of silently rendering.
export type Theme = 'dark'

interface ThemeContextType {
  theme: Theme
  /** Always true. */
  isDark: boolean
  /** Always false — kept so existing call sites compile. */
  isLight: boolean
  accentColor: string
  canvasBg: string
}

const ThemeContext = createContext<ThemeContextType | null>(null)

// Hoisted out of the Provider so the same reference is used every
// render. With this hoisted constant React's Object.is on the Provider
// value always returns true → consumers don't re-render → R3F's
// wrapContext bridge doesn't re-run Object.defineProperties.
const STATIC_DARK_VALUE: ThemeContextType = {
  theme: 'dark',
  isDark: true,
  isLight: false,
  accentColor: '#F1ECE6',
  canvasBg: '#221F1C',
}

interface ThemeProviderProps {
  children: ReactNode
}

export function ThemeProvider({ children }: ThemeProviderProps) {
  // Apply dark theme attribute once, drop any stale localStorage entry
  // from when the toggle still existed.
  useEffect(() => {
    document.documentElement.setAttribute('data-theme', 'dark')
    try { localStorage.removeItem('theme') } catch { /* ignore */ }
  }, [])

  // Values are constants — hoist out of the component so React's
  // Object.is sees the same reference on every render and consumers
  // don't get spurious re-renders that would propagate through R3F's
  // its-fine wrapContext bridge.
  return (
    <ThemeContext.Provider value={STATIC_DARK_VALUE}>
      {children}
    </ThemeContext.Provider>
  )
}

export function useTheme(): ThemeContextType {
  const context = useContext(ThemeContext)
  if (!context) {
    throw new Error('useTheme must be used within a ThemeProvider')
  }
  return context
}
